/**
 * Computes the five `--handyout-chart-1..5` slots from a single author colour.
 *
 * Everything here is pure maths on colour values: no DOM, no tokens, no
 * randomness, no global state. The same arguments always produce the same
 * strings.
 *
 * Why OKLCh and not HSL: HSL's `L` is not perceptual. Five HSL steps at a
 * fixed lightness produce wildly different perceived brightness across hues,
 * which is how a categorical palette ends up with a screaming yellow beside a
 * well-behaved blue. OKLab/OKLCh is perceptually uniform enough that holding
 * `L` really does hold apparent loudness, so every rule below is expressed in
 * OKLCh and the sRGB hex is only the output format.
 */

export type PaletteScheme = "analog" | "monochrome" | "grayscale";

/** The five slots, in `--handyout-chart-1..5` order. */
export type PaletteSlots = [string, string, string, string, string];

export interface ComputedPalette {
  light: string[];
  dark: string[];
}

/**
 * Verdict on the author's colour. A muted primary is reported, never quietly
 * saturated for them: there is no hidden chroma floor in this module, so the
 * author is the one who decides what to do about it.
 */
export type PrimaryAssessment = {
  ok: boolean;
  reason?: "too-muted";
  message?: string;
};

export interface Oklch {
  /** Perceptual lightness, 0 (black) to 1 (white). */
  readonly l: number;
  /** Chroma. Unbounded in principle; sRGB never exceeds ~0.323. */
  readonly c: number;
  /** Hue angle in degrees, 0 to 360. Meaningless when `c` is ~0. */
  readonly h: number;
}

/** Used whenever `primary` cannot be parsed. Matches the shipped `chart-1`. */
export const DEFAULT_PRIMARY = "#FF5101";

export const DEFAULT_SLOTS_LIGHT: PaletteSlots = [
  "#FF5101",
  "#0EA5E9",
  "#a3a3a3",
  "#10B981",
  "#8B5CF6",
];

/**
 * Measured from the shipped hand-picked palette. The chromatic slots live in a
 * tight band so no series reads louder than another; the one achromatic slot
 * sits just above the band's top in light and well above it in dark, where it
 * has to clear the whole dark text scale to stop reading as a label.
 */
const LIGHT_CHROMATIC_BAND = { lo: 0.606, hi: 0.696 } as const;
const LIGHT_ACHROMATIC_L = 0.715;
const DARK_CHROMATIC_BAND = { lo: 0.709, hi: 0.773 } as const;
const DARK_ACHROMATIC_L = 0.87;

/**
 * Holding lightness holds only half of apparent loudness; chroma carries the
 * other half. The sRGB ceiling is wildly uneven across hues — magenta reaches
 * 0.32 where cyan stops near 0.12 — so a palette built purely on relative
 * saturation lets a magenta slot hit 0.30 and shout down its neighbours even
 * though every slot sits in the same lightness band. These are the loudest
 * chroma the hand-picked palette allows in each fassung, and computed slots
 * are held to them. The one exemption is slot 1 while it sits inside the band,
 * because there it is the author's colour verbatim and not a computed slot.
 */
const LIGHT_CHROMA_CEILING = 0.22;
const DARK_CHROMA_CEILING = 0.176;

/** Slot 3 (index 2) is the deliberate quiet series and stays achromatic. */
const ANALOG_ACHROMATIC_INDEX = 2;
const ANALOG_CHROMATIC_INDICES = [0, 1, 3, 4] as const;

/**
 * Four chromatic slots spread across the sector inclusive of both ends, so the
 * step is `sector / 3`.
 */
const ANALOG_HUE_STEPS = 3;

/**
 * The sector is fixed and not the author's to choose: no slider, no degree
 * control, one width that separates four slots as far as they can be.
 *
 * The real minimum gap in a four-point spread is `min(step, 360 - 3 * step)`,
 * because the last slot also has to clear the first one the long way round.
 * That is maximised at a step of 90 degrees — the even four-way split — and
 * falls again above it: at a sector of 360 the offsets wrap, the last slot
 * lands byte-identical on the first and the palette silently loses a series.
 * 270 is therefore the widest useful sector and also the best one, so there is
 * nothing left to expose.
 */
const ANALOG_SECTOR = 270;
const ANALOG_HUE_STEP = ANALOG_SECTOR / ANALOG_HUE_STEPS;

/**
 * `monochrome` is a single hue, so lightness and chroma are the only channels
 * left to separate five series with. Five distinguishable steps do not fit in
 * the 0.090-wide light band or the 0.064-wide dark one, so monochrome widens
 * to its own grid. The cost is that equal loudness is given up: it is a
 * property of `analog`, not of the palette as a whole.
 *
 * Both grids stop short of the ends of their range. The light grid's darkest
 * rung stays off black so the hue survives, and the dark grid's floor of 0.560
 * still clears the `#171717` canvas at about 4:1.
 */
const MONO_LIGHT_GRID = [0.715, 0.62, 0.525, 0.43, 0.335] as const;
const MONO_DARK_GRID = [0.9, 0.815, 0.73, 0.645, 0.56] as const;

/**
 * The dark fassung pins slot 1 to the grid's middle rung: it is the only rung
 * that falls inside the measured dark chromatic band, so it is where the
 * author's hue still reads as their colour rather than as a tint.
 */
const MONO_DARK_PRIMARY_INDEX = 2;

/**
 * Relative chroma for slots 2..5, in the order they claim the leftover rungs
 * (lightest first). The lightest leftover rung is pulled down to 0.30 of the
 * author's saturation, which is what gives monochrome its own desaturated end
 * without reserving an achromatic slot the way `analog` does.
 */
const MONO_QUIET_RAMP = [0.3, 0.62, 0.86, 1] as const;

/**
 * Relative chroma below which the author is told their colour is too muted,
 * rather than having it quietly saturated for them.
 *
 * Derived from the ramp above, not guessed: 0.30 of the author's saturation is
 * what this module reserves for its deliberately desaturated slot. A primary
 * whose own relative chroma is already at or under that is therefore no louder
 * than the quiet end of a saturated one, so every slot lands in the quiet
 * range and the series has no loud end left to fade from. Measured against the
 * shipped colours this separates a muted `#64748b` (0.203) and `#b08080`
 * (0.249) from a merely low-ceiling hue such as `#0d9488` (0.981), which is
 * fully saturated for its hue and reads perfectly well.
 *
 * The test is relative and not absolute chroma because that is the quantity
 * the slots are built from: a teal that has spent its hue's entire sRGB range
 * is not muted, it is simply a teal.
 */
const MIN_PRIMARY_SATURATION = MONO_QUIET_RAMP[0];

/**
 * Existing `--handyout-chart-mono-1..5`, returned verbatim by `grayscale`.
 * The separate `--handyout-plot-mono-*` ramp is intentionally not unified with
 * this one and is not this function's output.
 */
const GRAYSCALE_LIGHT: PaletteSlots = [
  "#171717",
  "#525252",
  "#a3a3a3",
  "#d4d4d4",
  "#e5e5e5",
];
const GRAYSCALE_DARK: PaletteSlots = [
  "#f5f5f5",
  "#b5b5b5",
  "#626262",
  "#383838",
  "#2a2a2a",
];

/**
 * Below this chroma there is no hue to read off the author's colour and no
 * saturation to transpose onto the other slots, so `analog` and `monochrome`
 * both degenerate and hand back the grey ramp instead of inventing a hue.
 */
const ACHROMATIC_CHROMA = 0.008;

/** Linear-RGB slack allowed when testing whether a colour is inside sRGB. */
const GAMUT_EPSILON = 1e-5;

/** Bisection stops once the chroma interval is this narrow. */
const CHROMA_RESOLUTION = 1e-4;

/** No sRGB colour exceeds ~0.323 chroma in OKLCh; this is a safe ceiling. */
const MAX_SRGB_CHROMA = 0.4;

function srgbToLinear(value: number): number {
  return value <= 0.04045
    ? value / 12.92
    : Math.pow((value + 0.055) / 1.055, 2.4);
}

function linearToSrgb(value: number): number {
  return value <= 0.0031308
    ? value * 12.92
    : 1.055 * Math.pow(value, 1 / 2.4) - 0.055;
}

interface Rgb {
  r: number;
  g: number;
  b: number;
}

/** Ottosson's linear-sRGB to OKLab matrices, with the LMS cube root between. */
function linearRgbToOklab(rgb: Rgb): { l: number; a: number; b: number } {
  const lms0 =
    0.4122214708 * rgb.r + 0.5363325363 * rgb.g + 0.0514459929 * rgb.b;
  const lms1 =
    0.2119034982 * rgb.r + 0.6806995451 * rgb.g + 0.1073969566 * rgb.b;
  const lms2 =
    0.0883024619 * rgb.r + 0.2817188376 * rgb.g + 0.6299787005 * rgb.b;

  const c0 = Math.cbrt(lms0);
  const c1 = Math.cbrt(lms1);
  const c2 = Math.cbrt(lms2);

  return {
    l: 0.2104542553 * c0 + 0.793617785 * c1 - 0.0040720468 * c2,
    a: 1.9779984951 * c0 - 2.428592205 * c1 + 0.4505937099 * c2,
    b: 0.0259040371 * c0 + 0.7827717662 * c1 - 0.808675766 * c2,
  };
}

function oklabToLinearRgb(lab: { l: number; a: number; b: number }): Rgb {
  const c0 = lab.l + 0.3963377774 * lab.a + 0.2158037573 * lab.b;
  const c1 = lab.l - 0.1055613458 * lab.a - 0.0638541728 * lab.b;
  const c2 = lab.l - 0.0894841775 * lab.a - 1.291485548 * lab.b;

  const lms0 = c0 * c0 * c0;
  const lms1 = c1 * c1 * c1;
  const lms2 = c2 * c2 * c2;

  return {
    r: 4.0767416621 * lms0 - 3.3077115913 * lms1 + 0.2309699292 * lms2,
    g: -1.2684380046 * lms0 + 2.6097574011 * lms1 - 0.3413193965 * lms2,
    b: -0.0041960863 * lms0 - 0.7034186147 * lms1 + 1.707614701 * lms2,
  };
}

const HEX_PATTERN = /^#?([0-9a-f]{3,8})$/i;

function parseHex(value: string): Rgb | null {
  if (typeof value !== "string") return null;
  const match = HEX_PATTERN.exec(value.trim());
  if (!match) return null;
  const digits = match[1];
  let r: number;
  let g: number;
  let b: number;
  if (digits.length === 3 || digits.length === 4) {
    // Alpha, where present, is dropped: a palette slot is an opaque fill.
    r = parseInt(digits[0] + digits[0], 16);
    g = parseInt(digits[1] + digits[1], 16);
    b = parseInt(digits[2] + digits[2], 16);
  } else if (digits.length === 6 || digits.length === 8) {
    r = parseInt(digits.slice(0, 2), 16);
    g = parseInt(digits.slice(2, 4), 16);
    b = parseInt(digits.slice(4, 6), 16);
  } else {
    return null;
  }
  return { r: r / 255, g: g / 255, b: b / 255 };
}

/** True for `#rgb`, `#rgba`, `#rrggbb` and `#rrggbbaa`, with or without `#`. */
export function isValidHexColor(value: string): boolean {
  return parseHex(value) !== null;
}

function toHexByte(value: number): string {
  const byte = Math.round(Math.min(1, Math.max(0, value)) * 255);
  return byte.toString(16).padStart(2, "0");
}

/** Normalises an arbitrary hue angle, negatives included, into 0..360. */
export function normalizeHue(hue: number): number {
  if (!Number.isFinite(hue)) return 0;
  return ((hue % 360) + 360) % 360;
}

export function hexToOklch(hex: string): Oklch | null {
  const srgb = parseHex(hex);
  if (!srgb) return null;
  const lab = linearRgbToOklab({
    r: srgbToLinear(srgb.r),
    g: srgbToLinear(srgb.g),
    b: srgbToLinear(srgb.b),
  });
  const c = Math.sqrt(lab.a * lab.a + lab.b * lab.b);
  // atan2 of a near-zero vector is numerically meaningless, so a grey gets a
  // fixed hue of 0 rather than whatever the float noise happens to point at.
  const h =
    c < 1e-7 ? 0 : normalizeHue((Math.atan2(lab.b, lab.a) * 180) / Math.PI);
  return { l: lab.l, c, h };
}

function oklchToLinearRgb(color: Oklch): Rgb {
  const radians = (color.h * Math.PI) / 180;
  return oklabToLinearRgb({
    l: color.l,
    a: color.c * Math.cos(radians),
    b: color.c * Math.sin(radians),
  });
}

function isInGamut(color: Oklch): boolean {
  const rgb = oklchToLinearRgb(color);
  const lo = -GAMUT_EPSILON;
  const hi = 1 + GAMUT_EPSILON;
  return (
    rgb.r >= lo &&
    rgb.r <= hi &&
    rgb.g >= lo &&
    rgb.g <= hi &&
    rgb.b >= lo &&
    rgb.b <= hi
  );
}

/**
 * Largest chroma that still fits inside sRGB at this lightness and hue.
 *
 * This is the whole reason the module cannot pick one chroma number and apply
 * it to five hues: the ceiling varies enormously with hue. A green can never
 * be as saturated as a blue at the same lightness, so a fixed chroma either
 * clips the green, which converts to a hue-shifted and quietly desaturated
 * colour, or wastes most of the blue's range.
 *
 * Reducing chroma at fixed L and H is the only clamp that preserves both
 * lightness and hue exactly, which is why it is used instead of clamping the
 * RGB channels. Bisection, so the result is deterministic.
 */
export function maxChroma(lightness: number, hue: number): number {
  if (!(lightness > 0) || lightness >= 1) return 0;
  let lo = 0;
  let hi = MAX_SRGB_CHROMA;
  if (isInGamut({ l: lightness, c: hi, h: hue })) return hi;
  while (hi - lo > CHROMA_RESOLUTION) {
    const mid = (lo + hi) / 2;
    if (isInGamut({ l: lightness, c: mid, h: hue })) lo = mid;
    else hi = mid;
  }
  return lo;
}

/** Chroma as a fraction of what this lightness and hue can actually hold. */
function relativeChroma(color: Oklch): number {
  const ceiling = maxChroma(color.l, color.h);
  if (ceiling <= 0) return 0;
  return Math.min(1, color.c / ceiling);
}

/**
 * Lightness inside `[lo, hi]` at which this hue can hold the most chroma.
 *
 * This is what reproduces the shape of the hand-picked palette. Max chroma is
 * unimodal in lightness at a fixed hue, and its peak sits low for blues and
 * violets and high for yellows and greens. Taking the peak within the band
 * therefore lands violet near the band floor and yellow-green near its
 * ceiling, which is exactly where the hand-picked violet (L 0.606) and green
 * (L 0.696) sit. Ternary search on the unimodal curve.
 */
function bandLightnessForHue(
  hue: number,
  band: { lo: number; hi: number }
): number {
  let lo = band.lo;
  let hi = band.hi;
  for (let i = 0; i < 20; i += 1) {
    const third = (hi - lo) / 3;
    const left = lo + third;
    const right = hi - third;
    if (maxChroma(left, hue) < maxChroma(right, hue)) lo = left;
    else hi = right;
  }
  return (lo + hi) / 2;
}

function toHex(color: Oklch): string {
  const lightness = Math.min(1, Math.max(0, color.l));
  const hue = normalizeHue(color.h);
  const clamped: Oklch = {
    l: lightness,
    c: Math.max(0, Math.min(color.c, maxChroma(lightness, hue))),
    h: hue,
  };
  const linear = oklchToLinearRgb(clamped);
  return (
    "#" +
    toHexByte(linearToSrgb(linear.r)) +
    toHexByte(linearToSrgb(linear.g)) +
    toHexByte(linearToSrgb(linear.b))
  );
}

/** Canonical lowercase `#rrggbb`, so slot 1 is byte-identical to the input. */
function canonicalHex(srgb: Rgb): string {
  return (
    "#" + toHexByte(srgb.r) + toHexByte(srgb.g) + toHexByte(srgb.b)
  );
}

interface Fassung {
  readonly chromaticBand: { readonly lo: number; readonly hi: number };
  readonly achromaticL: number;
  readonly chromaCeiling: number;
  readonly monoGrid: readonly number[];
  /**
   * `monochrome` only: light pins slot 1 to the author's colour and gives up
   * the rung nearest it, dark has no pinned lightness to match a rung against.
   * `analog` decides per slot from the band instead.
   */
  readonly pinsPrimary: boolean;
}

const LIGHT_FASSUNG: Fassung = {
  chromaticBand: LIGHT_CHROMATIC_BAND,
  achromaticL: LIGHT_ACHROMATIC_L,
  chromaCeiling: LIGHT_CHROMA_CEILING,
  monoGrid: MONO_LIGHT_GRID,
  pinsPrimary: true,
};

const DARK_FASSUNG: Fassung = {
  chromaticBand: DARK_CHROMATIC_BAND,
  achromaticL: DARK_ACHROMATIC_L,
  chromaCeiling: DARK_CHROMA_CEILING,
  monoGrid: MONO_DARK_GRID,
  pinsPrimary: false,
};

/**
 * Chroma a slot should carry: the author's saturation transposed onto this
 * hue's own sRGB ceiling, then held under the fassung's loudness ceiling.
 * `weight` lets the monochrome ramp fade its quiet end.
 */
function slotChroma(
  lightness: number,
  hue: number,
  saturation: number,
  fassung: Fassung,
  weight = 1
): number {
  const ceiling = maxChroma(lightness, hue);
  return Math.min(saturation * weight * ceiling, fassung.chromaCeiling);
}

function computeAnalog(
  primary: Oklch,
  primaryHex: string,
  fassung: Fassung
): PaletteSlots {
  const saturation = relativeChroma(primary);
  const band = fassung.chromaticBand;
  const slots: string[] = ["", "", "", "", ""];

  ANALOG_CHROMATIC_INDICES.forEach((slotIndex, k) => {
    const hue = normalizeHue(primary.h + k * ANALOG_HUE_STEP);
    const lightness = bandLightnessForHue(hue, band);
    slots[slotIndex] = toHex({
      l: lightness,
      c: slotChroma(lightness, hue, saturation, fassung),
      h: hue,
    });
  });

  slots[ANALOG_ACHROMATIC_INDEX] = toHex({
    l: fassung.achromaticL,
    c: 0,
    h: 0,
  });

  // Slot 1 is the author's colour byte for byte in light as long as it is
  // readable, and readable is exactly what the band means: a primary above the
  // band's top vanishes against white. Outside the band the slot is nudged the
  // shortest distance back into it — nearest edge, hue untouched.
  //
  // Dark never pins. The decision is the author's: light shows the chosen
  // colour, dark shows a lightened version of it. That is also what the canvas
  // demands — a primary such as `#611E24` at L 0.342 disappears against
  // `#171717` — and it keeps the slot under the dark chroma ceiling, which a
  // verbatim pin would exempt it from.
  slots[0] =
    fassung.pinsPrimary && primary.l >= band.lo && primary.l <= band.hi
      ? primaryHex
      : nudgedPrimarySlot(primary, saturation, fassung);

  return slots as unknown as PaletteSlots;
}

function nudgedPrimarySlot(
  primary: Oklch,
  saturation: number,
  fassung: Fassung
): string {
  const band = fassung.chromaticBand;
  const lightness = Math.min(band.hi, Math.max(band.lo, primary.l));
  return toHex({
    l: lightness,
    c: slotChroma(lightness, primary.h, saturation, fassung),
    h: primary.h,
  });
}

function nearestIndex(values: readonly number[], target: number): number {
  let best = 0;
  let bestDistance = Infinity;
  for (let i = 0; i < values.length; i += 1) {
    const distance = Math.abs(values[i] - target);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = i;
    }
  }
  return best;
}

function computeMonochrome(
  primary: Oklch,
  primaryHex: string,
  fassung: Fassung
): PaletteSlots {
  const saturation = relativeChroma(primary);
  const grid = fassung.monoGrid;

  // Slot 1 claims one rung and the other four share what is left. In light the
  // rung is the one nearest the author's own lightness, so dropping it leaves
  // no near-duplicate of the pinned colour. In dark there is no pinned
  // lightness to match, so slot 1 takes the middle rung.
  const primaryRung = fassung.pinsPrimary
    ? nearestIndex(grid, primary.l)
    : MONO_DARK_PRIMARY_INDEX;

  const slots: string[] = ["", "", "", "", ""];
  slots[0] = fassung.pinsPrimary
    ? primaryHex
    : toHex({
        l: grid[primaryRung],
        c: slotChroma(grid[primaryRung], primary.h, saturation, fassung),
        h: primary.h,
      });

  let slot = 1;
  for (let rung = 0; rung < grid.length; rung += 1) {
    if (rung === primaryRung) continue;
    const lightness = grid[rung];
    slots[slot] = toHex({
      l: lightness,
      c: slotChroma(
        lightness,
        primary.h,
        saturation,
        fassung,
        MONO_QUIET_RAMP[slot - 1]
      ),
      h: primary.h,
    });
    slot += 1;
  }

  return slots as unknown as PaletteSlots;
}

// Fresh arrays per call: the returned slots are a plain mutable `string[]`, so
// handing out the module's own constants would let one caller edit the ramp for
// every other one.
function grayscalePalette(): ComputedPalette {
  return { light: [...GRAYSCALE_LIGHT], dark: [...GRAYSCALE_DARK] };
}

/**
 * The author's colour as both fassungen will read it, so the assessment and
 * the palette can never disagree about which colour was assessed.
 */
function readPrimary(primary: string): { color: Oklch; hex: string } | null {
  const srgb = parseHex(primary) ?? parseHex(DEFAULT_PRIMARY);
  if (!srgb) return null;
  const hex = canonicalHex(srgb);
  const color = hexToOklch(hex);
  return color ? { color, hex } : null;
}

const TOO_MUTED_MESSAGE =
  "This colour is not contrasty enough to build a five-series palette from. " +
  "Pick a more saturated version of it, or switch to the grayscale scheme.";

/**
 * Whether the author's colour can carry a palette, for the editor to show a
 * hint next to the colour input.
 *
 * This is the whole response to a muted primary: it is reported and left
 * alone. Nothing in this module raises an author's chroma to meet a floor,
 * because a palette silently louder than the colour they picked is a worse
 * outcome than being told the colour will not carry one.
 *
 * `ok` is not a promise that every slot is beautiful, only that the primary
 * has saturation to transpose. Unparseable input is not this function's
 * subject and reports `ok` — `computePalette` answers it with
 * `DEFAULT_PRIMARY`, and `isValidHexColor` is what rejects it in the editor.
 */
export function assessPrimary(primary: string): PrimaryAssessment {
  const read = readPrimary(primary);
  if (!read) return { ok: true };
  if (relativeChroma(read.color) < MIN_PRIMARY_SATURATION) {
    return { ok: false, reason: "too-muted", message: TOO_MUTED_MESSAGE };
  }
  return { ok: true };
}

/**
 * Computes both fassungen of the five-slot module palette.
 *
 * @param primary Author's hex colour. `#rgb`/`#rrggbb` with or without `#`;
 *   alpha is ignored. Anything unparseable falls back to `DEFAULT_PRIMARY`
 *   rather than throwing, because the render path must always receive five
 *   colours. Use `isValidHexColor` in the editor to reject bad input there.
 * @param scheme `analog` spreads hue across a fixed 270-degree sector and
 *   reserves slot 3 as the achromatic quiet series. `monochrome` is a true
 *   0 degrees — one hue, separated by lightness and chroma across a widened
 *   grid, its desaturated end taking the place of the achromatic slot.
 *   `grayscale` ignores `primary`. The sector is not a parameter: see
 *   `ANALOG_SECTOR` for why there is one right width and nothing to choose.
 *
 * Edge inputs: a primary whose chroma is under 0.008 carries no usable hue, so
 * `analog` and `monochrome` both return the grey ramp instead of inventing
 * one. Pure black and pure white fall into that case by definition. This
 * function never refuses input — it always returns five usable colours per
 * fassung, and `assessPrimary` is what tells the author when the colour they
 * picked is too muted to deserve them.
 */
export function computePalette(
  primary: string,
  scheme: PaletteScheme
): ComputedPalette {
  if (scheme === "grayscale") return grayscalePalette();

  const read = readPrimary(primary);
  if (!read || read.color.c < ACHROMATIC_CHROMA) return grayscalePalette();

  const { color, hex } = read;
  if (scheme === "monochrome") {
    return {
      light: computeMonochrome(color, hex, LIGHT_FASSUNG),
      dark: computeMonochrome(color, hex, DARK_FASSUNG),
    };
  }

  return {
    light: computeAnalog(color, hex, LIGHT_FASSUNG),
    dark: computeAnalog(color, hex, DARK_FASSUNG),
  };
}

export { computePalette as compute };

/** WCAG 2.x relative luminance of an sRGB hex. Returns 0 for bad input. */
export function relativeLuminance(hex: string): number {
  const srgb = parseHex(hex);
  if (!srgb) return 0;
  return (
    0.2126 * srgbToLinear(srgb.r) +
    0.7152 * srgbToLinear(srgb.g) +
    0.0722 * srgbToLinear(srgb.b)
  );
}

/** WCAG 2.x contrast ratio between two sRGB hexes, 1 to 21. */
export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

export const READABLE_LIGHT_FOREGROUND = "#FFFFFF";
export const READABLE_DARK_FOREGROUND = "#171717";

export function readableForeground(hex: string): string {
  return contrastRatio(hex, READABLE_LIGHT_FOREGROUND) >=
    contrastRatio(hex, READABLE_DARK_FOREGROUND)
    ? READABLE_LIGHT_FOREGROUND
    : READABLE_DARK_FOREGROUND;
}
